if (!customElements.get('product-recommendations')) {
  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.limit = Number(
        this.dataset.recommendationLimit ||
        (this.dataset.recommendationArea === 'cart-drawer' ? 3 : 8)
      );

      this.sourceFamily =
        this.dataset.sourceFamily || 'unknown';

      this.sourceSize =
        this.dataset.sourceSize || '';

      this.sourceCollections =
        this.pipeSet(this.dataset.sourceCollections);

      this.cartProductIds =
        this.pipeSet(this.dataset.cartProductIds);
    }

    connectedCallback() {
      if (this.dataset.recommendationsLoaded === 'true') return;

      this.dataset.recommendationsLoaded = 'true';
      this.performRecommendations();
    }

    pipeSet(value = '') {
      return new Set(
        String(value)
          .split('|')
          .map(item => item.trim())
          .filter(Boolean)
      );
    }

    normalizeSize(value = '') {
      const raw = String(value)
        .trim()
        .toLowerCase();

      if (!raw) return '';

      const alphaPattern =
        /(xxxs|xxs|xs|xxxl|xxl|xl|s|m|l)/i;

      const parenthetical =
        raw.match(
          new RegExp(`\\(\\s*${alphaPattern.source}\\s*\\)`, 'i')
        );

      if (parenthetical) {
        return parenthetical[1].toLowerCase();
      }

      const standalone =
        raw.match(
          new RegExp(
            `(?:^|[^a-z])${alphaPattern.source}(?:$|[^a-z])`,
            'i'
          )
        );

      if (standalone) {
        return standalone[1].toLowerCase();
      }

      const numeric =
        raw.match(/^\s*(\d{1,3})\s*$/);

      if (numeric) {
        return numeric[1];
      }

      return raw.replace(/[^a-z0-9]+/g, '');
    }

    sizeSystem(value = '') {
      const normalized = this.normalizeSize(value);

      if (!normalized) return 'none';

      if (
        /^(xxxs|xxs|xs|s|m|l|xl|xxl|xxxl)$/.test(
          normalized
        )
      ) {
        return 'alpha';
      }

      if (/^\d{1,3}$/.test(normalized)) {
        return 'numeric';
      }

      return 'other';
    }

    normalizedSizeSet(value = '') {
      return new Set(
        Array.from(this.pipeSet(value))
          .map(size => this.normalizeSize(size))
          .filter(Boolean)
      );
    }

    getProductId(item) {
      return String(
        item.dataset.productId ||
        item.getAttribute('href') ||
        ''
      );
    }

    getFamily(item) {
      return item.dataset.family || 'unknown';
    }

    isSizeEligible(item) {
      if (!this.sourceSize) return true;

      if (item.dataset.hasSizeOption !== 'true') {
        return true;
      }

      const sourceSize =
        this.normalizeSize(this.sourceSize);

      const sourceSystem =
        this.sizeSystem(this.sourceSize);

      if (!sourceSize || sourceSystem === 'none') {
        return true;
      }

      const allSizes =
        this.normalizedSizeSet(item.dataset.allSizes);

      const availableSizes =
        this.normalizedSizeSet(
          item.dataset.availableSizes
        );

      if (!allSizes.size) return true;

      const candidateSystems = new Set(
        Array.from(allSizes).map(
          size => this.sizeSystem(size)
        )
      );

      /*
        If both products use a comparable sizing system,
        the customer's selected size must actually exist and
        be available in the candidate.
      */
      if (
        sourceSystem !== 'other' &&
        candidateSystems.has(sourceSystem)
      ) {
        return (
          allSizes.has(sourceSize) &&
          availableSizes.has(sourceSize)
        );
      }

      /*
        Same-category products should normally use a comparable
        size system. If they do not, do not recommend them as a
        fallback substitute.
      */
      if (
        this.getFamily(item) === this.sourceFamily &&
        sourceSystem !== 'other'
      ) {
        return false;
      }

      /*
        Different category, different sizing system:
        e.g. S shirt -> 30/32 trouser. Product-level availability
        is the appropriate check here.
      */
      return true;
    }

    isEligible(item) {
      const productId = this.getProductId(item);

      if (!productId) return false;

      if (this.cartProductIds.has(productId)) {
        return false;
      }

      return this.isSizeEligible(item);
    }

    getFamilyPreferences() {
      const preferences = {
        shirts: [
          'trousers',
          'knitwear',
          'outerwear',
          'accessories'
        ],

        trousers: [
          'shirts',
          'knitwear',
          'outerwear',
          'accessories'
        ],

        knitwear: [
          'trousers',
          'shirts',
          'outerwear',
          'accessories'
        ],

        outerwear: [
          'knitwear',
          'trousers',
          'shirts',
          'accessories'
        ],

        jersey: [
          'trousers',
          'outerwear',
          'knitwear',
          'shirts'
        ],

        shorts: [
          'shirts',
          'jersey',
          'knitwear',
          'accessories'
        ],

        accessories: [
          'outerwear',
          'knitwear',
          'shirts',
          'trousers'
        ],

        unknown: [
          'trousers',
          'shirts',
          'knitwear',
          'outerwear',
          'jersey'
        ]
      };

      return (
        preferences[this.sourceFamily] ||
        preferences.unknown
      );
    }

    meaningfulSharedCollections(item) {
      const genericCollections = new Set([
        'all',
        'new-arrivals',
        'shirts',
        'trousers',
        'knitwear',
        'outerwear',
        'overshirts',
        'shorts',
        't-shirts',
        'sweatshirts',
        'jersey',
        'accessories',
        'hats',
        'socks',
        'scarves',
        'sale'
      ]);

      const candidateCollections =
        this.pipeSet(item.dataset.collections);

      let matches = 0;

      candidateCollections.forEach(handle => {
        if (
          this.sourceCollections.has(handle) &&
          !genericCollections.has(handle)
        ) {
          matches += 1;
        }
      });

      return matches;
    }

    sizeMatchBonus(item) {
      if (!this.sourceSize) return 0;

      const sourceSize =
        this.normalizeSize(this.sourceSize);

      const availableSizes =
        this.normalizedSizeSet(
          item.dataset.availableSizes
        );

      return availableSizes.has(sourceSize)
        ? 12
        : 0;
    }

    randomJitter(productId) {
      /*
        One seed per page load:
        suggestions remain stable while the customer is looking
        at the page, but can vary on a later visit/reload.
      */
      if (!window.faRecommendationSeed) {
        window.faRecommendationSeed =
          Math.floor(Math.random() * 1000000000);
      }

      const input =
        `${productId}-${window.faRecommendationSeed}`;

      let hash = 0;

      for (let index = 0; index < input.length; index += 1) {
        hash =
          ((hash << 5) - hash) +
          input.charCodeAt(index);

        hash |= 0;
      }

      return Math.abs(hash % 1200) / 100;
    }

    scoreFallback(item) {
      const family = this.getFamily(item);

      /*
        Same-category alternatives are not useful cart cross-sells.
        Accessories are the exception because two accessories can
        genuinely complement each other.
      */
      if (
        family === this.sourceFamily &&
        this.sourceFamily !== 'accessories'
      ) {
        return -Infinity;
      }

      const preferences =
        this.getFamilyPreferences();

      const preferenceIndex =
        preferences.indexOf(family);

      let score = 0;

      if (preferenceIndex !== -1) {
        score += 90 - (preferenceIndex * 12);
      } else if (
        family !== 'unknown' &&
        family !== this.sourceFamily
      ) {
        score += 35;
      }

      score +=
        this.meaningfulSharedCollections(item) * 24;

      score +=
        this.sizeMatchBonus(item);

      score +=
        this.randomJitter(this.getProductId(item));

      return score;
    }

    parseRecommendationResponse(text) {
      const documentHTML =
        new DOMParser().parseFromString(
          text,
          'text/html'
        );

      const responseComponent =
        documentHTML.querySelector(
          `product-recommendations[data-recommendation-area="${this.dataset.recommendationArea}"]`
        ) ||
        documentHTML.querySelector(
          'product-recommendations'
        );

      if (!responseComponent) {
        return {
          complementary: [],
          fallback: []
        };
      }

      const complementary =
        Array.from(
          responseComponent.querySelectorAll(
            '[data-recommendations] > .product-recommendations__item'
          )
        );

      const fallbackTemplate =
        responseComponent.querySelector(
          'template[data-fallback-candidates]'
        );

      const fallback =
        fallbackTemplate
          ? Array.from(
              fallbackTemplate.content.querySelectorAll(
                '.product-recommendations__item'
              )
            )
          : [];

      return {
        complementary,
        fallback
      };
    }

    async fetchCandidates() {
      if (!this.dataset.url) {
        return {
          complementary: [],
          fallback: []
        };
      }

      try {
        const response =
          await fetch(this.dataset.url);

        if (!response.ok) {
          return {
            complementary: [],
            fallback: []
          };
        }

        const text =
          await response.text();

        return this.parseRecommendationResponse(text);
      } catch (error) {
        console.error(
          'Product recommendations could not be loaded:',
          error
        );

        return {
          complementary: [],
          fallback: []
        };
      }
    }

    selectRecommendations(
      complementaryItems,
      fallbackItems
    ) {
      const selected = [];
      const selectedIds = new Set();
      const selectedFamilies = new Set();

      const addItem = item => {
        if (selected.length >= this.limit) {
          return false;
        }

        const productId =
          this.getProductId(item);

        if (
          !productId ||
          selectedIds.has(productId) ||
          !this.isEligible(item)
        ) {
          return false;
        }

        selected.push(item);
        selectedIds.add(productId);
        selectedFamilies.add(
          this.getFamily(item)
        );

        return true;
      };

      /*
        TIER 1
        Human-curated Search & Discovery complements.
        Preserve Shopify's manual order exactly.
      */
      complementaryItems.forEach(item => {
        addItem(item);
      });

      if (selected.length >= this.limit) {
        return selected;
      }

      /*
        TIER 2
        Cross-category fallback candidates.

        Score first, then prefer a different category for each
        additional slot so we don't swap "three shirts" for
        "three trousers".
      */
      const scoredFallback =
        fallbackItems
          .filter(item => {
            const productId =
              this.getProductId(item);

            return (
              productId &&
              !selectedIds.has(productId) &&
              this.isEligible(item)
            );
          })
          .map(item => ({
            item,
            score: this.scoreFallback(item)
          }))
          .filter(candidate =>
            Number.isFinite(candidate.score) &&
            candidate.score >= 50
          )
          .sort(
            (a, b) => b.score - a.score
          );

      /*
        First pass: maximise category diversity.
      */
      scoredFallback.forEach(candidate => {
        if (selected.length >= this.limit) return;

        const family =
          this.getFamily(candidate.item);

        if (
          family !== 'unknown' &&
          selectedFamilies.has(family)
        ) {
          return;
        }

        addItem(candidate.item);
      });

      /*
        Second pass: if a genuinely relevant candidate remains,
        allow another product from an already represented family.
        We still never force a weak recommendation just to hit 3.
      */
      if (selected.length < this.limit) {
        scoredFallback.forEach(candidate => {
          if (selected.length >= this.limit) return;

          addItem(candidate.item);
        });
      }

      return selected;
    }

    async performRecommendations() {
      const recommendations =
        this.querySelector(
          '[data-recommendations]'
        );

      if (!recommendations) return;

      const {
        complementary,
        fallback
      } = await this.fetchCandidates();

      const selected =
        this.selectRecommendations(
          complementary,
          fallback
        );

      if (!selected.length) {
        return;
      }

      recommendations.innerHTML = '';

      selected.forEach(item => {
        recommendations.appendChild(item);
      });

      this.classList.remove('hidden');

      const cartDrawerItemsFull =
        document.querySelector(
          '.cart-drawer-items__full'
        );

      if (cartDrawerItemsFull) {
        cartDrawerItemsFull.classList.remove(
          'cart-drawer-items__full'
        );
      }

      this.initSlider();

      /*
        Quick-add already exists globally. Re-initialise only
        after the selected recommendation nodes are in the DOM.
      */
      setTimeout(() => {
        const quickCartDrawer =
          document.querySelector(
            'quick-cart-drawer'
          );

        if (quickCartDrawer) {
          quickCartDrawer.init();
        } else {
          this.querySelectorAll(
            '.quick-cart-drawer__trigger'
          ).forEach(element => {
            element.remove();
          });
        }
      }, 250);
    }

    initSlider() {
      const swiper =
        this.querySelector('.swiper');

      if (!swiper) return;

      if (this.slider) {
        this.slider.destroy(
          true,
          true
        );
      }

      this.slider = new Swiper(swiper, {
        slidesPerView: 'auto',
        spaceBetween: 16
      });
    }
  }

  customElements.define(
    'product-recommendations',
    ProductRecommendations
  );
}